import { MMKV } from 'react-native-mmkv';

import {
  RECOVERY_STATE_SCHEMA_VERSION,
  TERMINAL_PENDING_RECOVERY_CLASSIFICATIONS,
  type PendingRecoveryItem,
  type PersistedValidatedReceipt,
  type RecoveryClock,
  type RecoveryEscrowProgress,
  type RecoveryGateDecision,
  type RecoveryIdentity,
  type RecoveryPhase,
  type RecoveryState,
  type RecoveryStateStore,
} from '@/types/recovery';
import { normalizeRecoveryUserError } from './recoverySafeErrors';

interface MMKVLike {
  getString(key: string): string | undefined;
  set(key: string, value: string): void;
}

const DEFAULT_STORAGE_ID = 'app-guincho-recovery-state';
const STORAGE_KEY_PREFIX = 'recovery_state_v1';

const phaseTransitions: Record<RecoveryPhase, readonly RecoveryPhase[]> = {
  capture_pending: ['capturing'],
  capturing: ['capturing', 'captured'],
  captured: ['uploading'],
  uploading: ['uploading', 'awaiting_validation'],
  awaiting_validation: ['awaiting_validation', 'validated'],
  validated: ['reset_authorized'],
  reset_authorized: ['pending_archived'],
  pending_archived: ['local_cleared'],
  local_cleared: ['canonical_pulled'],
  canonical_pulled: ['history_restored'],
  history_restored: ['verifying'],
  verifying: ['verifying', 'completed'],
  completed: ['completed'],
};

const defaultClock: RecoveryClock = { now: () => new Date().toISOString() };

const assertIdentity = (identity: RecoveryIdentity): void => {
  if (!identity.companyId.trim() || !identity.userId.trim() || !identity.deviceId.trim()) {
    throw new Error('Identidade de recuperacao incompleta.');
  }
};

const identityEquals = (left: RecoveryIdentity, right: RecoveryIdentity): boolean =>
  left.companyId === right.companyId &&
  left.userId === right.userId &&
  left.deviceId === right.deviceId;

const encodeStorageSegment = (value: string): string =>
  Array.from(value)
    .map(character => character.codePointAt(0)!.toString(16).padStart(6, '0'))
    .join('');

export const buildRecoveryStateStorageKey = (identity: RecoveryIdentity): string => {
  assertIdentity(identity);
  return [
    STORAGE_KEY_PREFIX,
    encodeStorageSegment(identity.companyId),
    encodeStorageSegment(identity.userId),
    encodeStorageSegment(identity.deviceId),
  ].join('.');
};

const assertPersistedState = (
  state: RecoveryState,
  identity: RecoveryIdentity
): RecoveryState => {
  if (state.schemaVersion !== RECOVERY_STATE_SCHEMA_VERSION) {
    throw new Error('Versao do estado de recuperacao nao suportada.');
  }
  if (!identityEquals(state.identity, identity)) {
    throw new Error('Estado de recuperacao pertence a outra identidade.');
  }
  if (!state.recoveryId?.trim() || !phaseTransitions[state.phase]) {
    throw new Error('Estado de recuperacao invalido.');
  }
  return state;
};

export class MMKVRecoveryStateStore implements RecoveryStateStore {
  private readonly storage: MMKVLike;

  constructor(storage?: MMKVLike) {
    this.storage = storage ?? new MMKV({ id: DEFAULT_STORAGE_ID });
  }

  async load(identity: RecoveryIdentity): Promise<RecoveryState | null> {
    const raw = this.storage.getString(buildRecoveryStateStorageKey(identity));
    if (!raw) return null;
    return assertPersistedState(JSON.parse(raw) as RecoveryState, identity);
  }

  async save(identity: RecoveryIdentity, state: RecoveryState): Promise<void> {
    assertPersistedState(state, identity);
    this.storage.set(buildRecoveryStateStorageKey(identity), JSON.stringify(state));
  }
}

export const countUnaccountedPending = (state: RecoveryState): number =>
  Object.values(state.pendingItems).filter(
    item => item.classification === 'unaccounted'
  ).length;

const isTerminalClassification = (classification: PendingRecoveryItem['classification']): boolean =>
  (TERMINAL_PENDING_RECOVERY_CLASSIFICATIONS as readonly string[]).includes(classification);

export class RecoveryGateService {
  constructor(
    private readonly store: RecoveryStateStore = new MMKVRecoveryStateStore(),
    private readonly clock: RecoveryClock = defaultClock
  ) {}

  async getState(identity: RecoveryIdentity): Promise<RecoveryState | null> {
    assertIdentity(identity);
    return this.store.load(identity);
  }

  async hydrateAndEvaluate(identity: RecoveryIdentity): Promise<RecoveryGateDecision> {
    try {
      const state = await this.getState(identity);
      if (!state) {
        return {
          blocked: false,
          phase: 'none',
          recoveryId: null,
          unaccountedPendingCount: 0,
          reasons: [],
        };
      }

      const unaccountedPendingCount = countUnaccountedPending(state);
      return {
        blocked: state.phase !== 'completed',
        phase: state.phase,
        recoveryId: state.recoveryId,
        unaccountedPendingCount,
        reasons: state.phase === 'completed' ? [] : ['recovery_in_progress'],
      };
    } catch {
      return {
        blocked: true,
        phase: 'unreadable',
        recoveryId: null,
        unaccountedPendingCount: 0,
        reasons: ['recovery_state_unreadable'],
      };
    }
  }

  async beginRecovery(input: {
    identity: RecoveryIdentity;
    recoveryId: string;
    consentedAt: string;
    pendingItems: Array<Pick<PendingRecoveryItem, 'pendingId' | 'sourceDigest'>>;
  }): Promise<RecoveryState> {
    const { identity, recoveryId, consentedAt, pendingItems } = input;
    assertIdentity(identity);
    if (!recoveryId.trim()) throw new Error('Recovery id ausente.');
    if (!consentedAt.trim() || Number.isNaN(Date.parse(consentedAt))) {
      throw new Error('Consentimento explicito ausente ou invalido.');
    }

    const existing = await this.store.load(identity);
    if (existing) {
      if (existing.recoveryId !== recoveryId) {
        throw new Error('Ja existe uma recuperacao ativa para este aparelho.');
      }
      this.assertPendingItemsMatch(existing, pendingItems);
      return existing;
    }

    const now = this.clock.now();
    const pendingRecord = pendingItems.reduce<Record<string, PendingRecoveryItem>>(
      (record, item) => {
        if (!item.pendingId.trim() || !item.sourceDigest.trim()) {
          throw new Error('Pendencia sem identificador ou digest.');
        }
        if (record[item.pendingId]) throw new Error('Pendencia duplicada na recuperacao.');
        record[item.pendingId] = {
          pendingId: item.pendingId,
          sourceDigest: item.sourceDigest,
          classification: 'unaccounted',
          classifiedAt: null,
          evidenceRef: null,
          reason: null,
        };
        return record;
      },
      {}
    );

    const state: RecoveryState = {
      schemaVersion: RECOVERY_STATE_SCHEMA_VERSION,
      identity,
      recoveryId,
      phase: 'capture_pending',
      createdAt: now,
      updatedAt: now,
      consentedAt,
      pendingItems: pendingRecord,
      escrow: null,
      validatedReceipt: null,
      lastError: null,
    };
    await this.store.save(identity, state);
    return state;
  }

  async transition(
    identity: RecoveryIdentity,
    recoveryId: string,
    nextPhase: RecoveryPhase
  ): Promise<RecoveryState> {
    return this.mutate(identity, recoveryId, state => {
      if (!phaseTransitions[state.phase].includes(nextPhase)) {
        throw new Error(`Transicao de recuperacao invalida: ${state.phase} -> ${nextPhase}.`);
      }
      if (nextPhase === 'validated' && !state.validatedReceipt) {
        throw new Error('Receipt remoto validado ausente.');
      }
      if (nextPhase === 'reset_authorized' && countUnaccountedPending(state) > 0) {
        throw new Error('Existem pendencias nao contabilizadas.');
      }
      return { ...state, phase: nextPhase, lastError: null };
    });
  }

  async setPreparedEscrow(
    identity: RecoveryIdentity,
    recoveryId: string,
    escrow: RecoveryEscrowProgress
  ): Promise<RecoveryState> {
    return this.mutate(identity, recoveryId, state => {
      if (state.phase !== 'capturing' && state.phase !== 'captured') {
        throw new Error('Escrow nao pode ser preparado nesta fase.');
      }
      return {
        ...state,
        phase: 'captured',
        escrow,
        lastError: null,
      };
    });
  }

  async markPartVerified(
    identity: RecoveryIdentity,
    recoveryId: string,
    partId: string
  ): Promise<RecoveryState> {
    return this.mutate(identity, recoveryId, state => {
      if (state.phase !== 'uploading') {
        throw new Error('Parte nao pode ser confirmada nesta fase.');
      }
      if (!state.escrow) throw new Error('Escrow ainda nao preparado.');
      const knownPart = state.escrow.prepared.manifest.parts.some(part => part.partId === partId);
      if (!knownPart) throw new Error('Parte desconhecida no escrow.');
      const verifiedPartIds = [...new Set([...state.escrow.verifiedPartIds, partId])];
      return {
        ...state,
        phase: 'uploading',
        escrow: { ...state.escrow, verifiedPartIds },
        lastError: null,
      };
    });
  }

  async markManifestVerified(
    identity: RecoveryIdentity,
    recoveryId: string
  ): Promise<RecoveryState> {
    return this.mutate(identity, recoveryId, state => {
      if (state.phase !== 'uploading') {
        throw new Error('Manifesto nao pode ser confirmado nesta fase.');
      }
      if (!state.escrow) throw new Error('Escrow ainda nao preparado.');
      if (state.escrow.verifiedPartIds.length !== state.escrow.prepared.manifest.parts.length) {
        throw new Error('Nem todas as partes do escrow foram verificadas.');
      }
      return {
        ...state,
        phase: 'uploading',
        escrow: { ...state.escrow, manifestVerified: true },
        lastError: null,
      };
    });
  }

  async markValidationRequested(
    identity: RecoveryIdentity,
    recoveryId: string
  ): Promise<RecoveryState> {
    return this.mutate(identity, recoveryId, state => {
      if (state.phase !== 'uploading' && state.phase !== 'awaiting_validation') {
        throw new Error('Validacao nao pode ser solicitada nesta fase.');
      }
      if (!state.escrow?.manifestVerified) {
        throw new Error('Manifesto remoto ainda nao verificado.');
      }
      return {
        ...state,
        phase: 'awaiting_validation',
        escrow: {
          ...state.escrow,
          validationRequestedAt: state.escrow.validationRequestedAt ?? this.clock.now(),
        },
        lastError: null,
      };
    });
  }

  async recordValidatedReceipt(
    identity: RecoveryIdentity,
    recoveryId: string,
    validatedReceipt: PersistedValidatedReceipt
  ): Promise<RecoveryState> {
    return this.mutate(identity, recoveryId, state => {
      const { receipt, verification } = validatedReceipt;
      if (
        state.phase !== 'awaiting_validation' ||
        !verification.valid ||
        !verification.verifiedAt?.trim() ||
        !verification.authority?.trim() ||
        !verification.proofDigest?.trim() ||
        !receipt.proof?.trim() ||
        verification.authority !== receipt.authority ||
        !state.escrow
      ) {
        throw new Error('Receipt nao possui validacao confiavel.');
      }
      if (
        receipt.recoveryId !== recoveryId ||
        receipt.companyId !== identity.companyId ||
        receipt.userId !== identity.userId ||
        receipt.deviceId !== identity.deviceId ||
        receipt.manifestSha512 !== state.escrow.prepared.manifestObject.ciphertextSha512
      ) {
        throw new Error('Receipt remoto nao corresponde ao escrow local.');
      }
      return {
        ...state,
        validatedReceipt,
        phase: 'validated',
        lastError: null,
      };
    });
  }

  async classifyPendingItems(
    identity: RecoveryIdentity,
    recoveryId: string,
    classifications: Array<{
      pendingId: string;
      classification: PendingRecoveryItem['classification'];
      evidenceRef: string;
      reason?: string;
    }>
  ): Promise<RecoveryState> {
    return this.mutate(identity, recoveryId, state => {
      const pendingItems = { ...state.pendingItems };
      for (const classification of classifications) {
        const existing = pendingItems[classification.pendingId];
        if (!existing) throw new Error('Pendencia desconhecida na classificacao.');
        if (!isTerminalClassification(classification.classification)) {
          throw new Error('Classificacao nao terminal nao pode arquivar uma pendencia.');
        }
        if (!classification.evidenceRef.trim()) {
          throw new Error('Classificacao terminal exige evidencia.');
        }
        if (
          classification.classification === 'escrowed_verified' &&
          !state.validatedReceipt
        ) {
          throw new Error('Pendencia nao pode usar escrow sem receipt validado.');
        }
        if (existing.classification !== 'unaccounted') {
          if (
            existing.classification !== classification.classification ||
            existing.evidenceRef !== classification.evidenceRef
          ) {
            throw new Error('Classificacao terminal de pendencia e imutavel.');
          }
          continue;
        }
        pendingItems[classification.pendingId] = {
          ...existing,
          classification: classification.classification,
          classifiedAt: this.clock.now(),
          evidenceRef: classification.evidenceRef,
          reason: classification.reason ?? null,
        };
      }
      return { ...state, pendingItems, lastError: null };
    });
  }

  async recordError(
    identity: RecoveryIdentity,
    recoveryId: string,
    error: unknown
  ): Promise<RecoveryState> {
    const rawMessage = error instanceof Error ? error.message : error;
    return this.mutate(identity, recoveryId, state => ({
      ...state,
      lastError: {
        phase: state.phase,
        message: normalizeRecoveryUserError(rawMessage),
        occurredAt: this.clock.now(),
      },
    }));
  }

  private async mutate(
    identity: RecoveryIdentity,
    recoveryId: string,
    updater: (state: RecoveryState) => RecoveryState
  ): Promise<RecoveryState> {
    const state = await this.store.load(identity);
    if (!state || state.recoveryId !== recoveryId) {
      throw new Error('Recuperacao ativa nao encontrada.');
    }
    const next = { ...updater(state), updatedAt: this.clock.now() };
    await this.store.save(identity, next);
    return next;
  }

  private assertPendingItemsMatch(
    state: RecoveryState,
    pendingItems: Array<Pick<PendingRecoveryItem, 'pendingId' | 'sourceDigest'>>
  ): void {
    const existingIds = Object.keys(state.pendingItems).sort();
    const incomingIds = pendingItems.map(item => item.pendingId).sort();
    if (existingIds.join('|') !== incomingIds.join('|')) {
      throw new Error('Lista de pendencias diverge da recuperacao persistida.');
    }
    for (const item of pendingItems) {
      if (state.pendingItems[item.pendingId]?.sourceDigest !== item.sourceDigest) {
        throw new Error('Conteudo de pendencia diverge da recuperacao persistida.');
      }
    }
  }
}
